import type {
  AdminUserQuery,
  AuditQuery,
  FeedbackCategory,
  HelpQuery,
  HelpRequestInput,
  HelpStatus,
  HelpType,
  FeedbackInput,
  FeedbackQuery,
  FeedbackStatus,
  ContentSource,
  QuizOverlayView,
  ReuseKind,
  ReusePolicy,
  Audience,
  AudienceScope,
  MemberActivity,
  MemberQuery,
  EventInput,
  EventKind,
  EventSettings,
  RegistrationStatus,
  SessionFormat,
  CatalogQuery,
  CatalogStatus,
  CatalogType,
  Level,
  Topic,
  AiJob,
  AiJobRequest,
  ApplyOutline,
  AdminCreateOrgInput,
  AdminUpdateOrgInput,
  ApproveOrgRequestInput,
  CreateInviteCodeInput,
  GroupInput,
  InvitationInput,
  JoinRequestReason,
  MemberRoleInput,
  CreateOrgInput,
  CreatorGrantInput,
  Answer,
  FrozenQuestion,
  LimitKey,
  QuestionBankInput,
  QuestionInput,
  QuizInput,
  QuizAudience,
  QuizMode,
  QuizResult,
  QuizSettings,
  Skill,
  TakerQuestion,
  Limits,
  OrgRequestInput,
  OrgType,
  Plan,
  PlanId,
  Role,
  AssetKind,
  Block,
  CourseInput,
  CourseOutline,
  CourseSettings,
  LearnerAsset,
  LearnerBlock,
  LessonInput,
  LessonProgressState,
} from '@ready/shared';
import { yearMonthIn } from '@ready/shared';
import type {
  LearningPath,
  OrgBrand,
  OrgDirectoryQuery,
  OrgProfileInput,
  OrgSocialLinks,
  PublicPathItem,
} from '@ready/shared';
import { useAuth } from './auth';
import { browserTimeZone } from './dates';

const BASE = import.meta.env.VITE_API_URL ?? '';

/** A path on the API as a URL the browser can load (images, downloads). */
export const apiUrl = (path: string) => `${BASE}${path}`;

/**
 * The message from an API error body: a string from our handlers, or (for
 * request validation) the first zod issue, e.g. "meetingUrl: Use an https:// link".
 */
function errorMessage(
  error: string | { issues?: { message?: string; path?: (string | number)[] }[] } | undefined,
  status: number,
): string {
  if (typeof error === 'string') return error;
  const issue = error?.issues?.[0];
  if (issue?.message)
    return issue.path?.length ? `${issue.path.join('.')}: ${issue.message}` : issue.message;
  return `Request failed (${status})`;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Set when a plan limit blocked the request. */
    readonly limit?: LimitKey,
  ) {
    super(message);
  }
}

export interface NameStatus {
  certificates: number;
  /** Self-service corrections left once certificates exist (0 or 1). */
  correctionsLeft: number;
  /** True when only a platform admin can change the name. */
  locked: boolean;
}
export interface Me {
  id: string;
  email: string;
  displayName: string | null;
  /** IANA zone saved on the profile; null until the app first reports one. */
  timeZone: string | null;
  /** The learning path picked on Home; null until chosen. */
  learningPath: LearningPath | null;
  isPlatformAdmin: boolean;
  name: NameStatus;
  /** null: must request an org. */
  creator:
    | { unlimited: true; maxOrgs: null; used: null; planId: null }
    | { unlimited: false; maxOrgs: number; used: number; planId: PlanId }
    | null;
}
export interface TreeUsage {
  rootOrgId: string;
  rootOrgName: string;
  planId: PlanId;
  planName: string;
  limits: Limits;
  usage: Limits;
}
export type OrgRequestStatus = 'pending' | 'approved' | 'rejected' | 'canceled';
export interface OrgRequest {
  id: string;
  name: string;
  type: OrgType;
  country: string;
  website: string | null;
  domain: string | null;
  expectedMembers: number;
  message: string | null;
  status: OrgRequestStatus;
  decisionNote: string | null;
  orgId: string | null;
  createdAt: string;
  decidedAt: string | null;
}
export interface AdminOrgRequest extends OrgRequest {
  requesterEmail: string;
  signals: { emailMatchesDomain: boolean; domainTakenBy: string | null };
}
export interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  isPlatformAdmin: boolean;
  suspendedAt: string | null;
  createdAt: string;
  creatorGrant: { maxOrgs: number; planId: PlanId; reason: string | null } | null;
  /** Active memberships, and the first few org names (top-level first). */
  orgCount: number;
  orgNames: string[];
  /** Engagement across the platform. */
  coursesStarted: number;
  coursesCompleted: number;
  quizzesTaken: number;
  quizzesPassed: number;
  eventsAttended: number;
  certificates: number;
  lastActiveAt: string | null;
  activity: MemberActivity;
}
export interface OrgOption {
  id: string;
  name: string;
  depth: number;
  archived: boolean;
}
export interface AdminOrg extends Org, TreeUsage {
  archivedAt: string | null;
  limitOverrides: Partial<Limits> | null;
  createdAt: string;
}
export interface Question extends Omit<FrozenQuestion, 'explanation'> {
  explanation: string | null;
  bankId: string;
  ownerOrgId: string | null;
  createdAt: string;
}
export interface QuestionBank {
  id: string;
  ownerOrgId: string | null;
  title: string;
  description: string | null;
}
export interface QuestionBankSummary extends QuestionBank {
  questionCount: number;
  /** False for Nirvik library banks shown to an org (read-only). */
  editable: boolean;
}
/** A question as listed in a quiz draft, with the bank it came from. */
export interface QuizQuestionRef {
  id: string;
  prompt: string;
  skill: Skill;
  bankId: string;
  bankTitle: string;
}
export interface Quiz {
  id: string;
  ownerOrgId: string | null;
  slug: string;
  title: string;
  description: string | null;
  mode: QuizMode;
  /** `course`: a course's final assessment, only taken inside that course. */
  audience: QuizAudience;
  audienceScope?: AudienceScope | null;
  settings: QuizSettings;
  level: Level;
  topics: Topic[];
  tags: string[];
  status: 'draft' | 'published' | 'archived';
  reuse?: ReusePolicy | null;
  clonedFromId?: string | null;
  updatedAt: string;
}
// ─── Courses ───────────────────────────────────────────────────────────

export interface CourseSummary {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  status: 'draft' | 'published' | 'archived';
  audience: Audience;
  audienceScope?: AudienceScope | null;
  draftChangedAt: string | null;
  updatedAt: string;
  lessonCount: number;
}
export interface Course {
  id: string;
  ownerOrgId: string | null;
  slug: string;
  title: string;
  summary: string | null;
  description: string | null;
  outcomes: string[];
  level: 'beginner' | 'intermediate' | 'advanced';
  estimatedMinutes: number | null;
  tags: string[];
  topics: Topic[];
  coverAssetId: string | null;
  audience: Audience;
  audienceScope?: AudienceScope | null;
  settings: CourseSettings;
  outline: CourseOutline;
  status: 'draft' | 'published' | 'archived';
  draftChangedAt: string | null;
  reuse?: ReusePolicy | null;
  clonedFromId: string | null;
  updatedAt: string;
}
export interface DraftLessonSummary {
  id: string;
  title: string;
  /** A customized course's lesson from its source (not editable here; title and note are). */
  fromSource?: boolean;
  sourceTitle?: string;
  note?: string | null;
  summary: string | null;
  optional: boolean;
  estimatedMinutes: number | null;
  blockCount: number;
  updatedAt: string;
}
export interface DraftLesson extends Omit<DraftLessonSummary, 'blockCount'> {
  courseId: string;
  blocks: Block[];
}
export interface CourseDraft {
  course: Course;
  lessons: DraftLessonSummary[];
  latestVersion: { version: number; publishedAt: string } | null;
  /** Where this copy came from, if it is one. */
  source: ContentSource | null;
  /** A customized course: what it left out that the source still has. */
  overlay: {
    sourceVersion: number | null;
    hiddenLessons: { id: string; title: string; moduleId: string }[];
    hiddenModules: { id: string; title: string }[];
  } | null;
}
export interface CourseFile {
  id: string;
  kind: AssetKind;
  filename: string;
  contentType: string;
  sizeBytes: number;
  status: 'uploading' | 'scanning' | 'ready' | 'infected' | 'failed';
  previewStatus: 'none' | 'pending' | 'ready' | 'failed';
  courseId: string | null;
  createdAt: string;
  url: string | null;
  previewUrl: string | null;
}
export interface PublicCourse {
  slug: string;
  title: string;
  summary: string | null;
  description: string | null;
  outcomes: string[];
  level: string;
  estimatedMinutes: number | null;
  tags: string[];
  audience: Audience;
  audienceScope?: AudienceScope | null;
  issuerName: string;
  /** The offering org; null for Nirvik's library. */
  offeredBy: OrgBrand | null;
  certificate: boolean;
  hasFinalQuiz: boolean;
  coverUrl: string | null;
  modules: Array<{
    id: string;
    title: string;
    summary: string | null;
    lessons: Array<{
      id: string;
      title: string;
      summary: string | null;
      optional: boolean;
      estimatedMinutes: number | null;
    }>;
  }>;
}
export interface LearnerCourse {
  slug: string;
  title: string;
  issuerName: string;
  version: number;
  newerVersion: number | null;
  enrolledAt: string;
  completedAt: string | null;
  lastLessonId: string | null;
  nextLessonId: string | null;
  progress: { completed: number; required: number };
  finalQuiz: CourseAssessmentState | null;
  certificate: boolean;
  certificateCode: string | null;
  modules: Array<{
    id: string;
    title: string;
    summary: string | null;
    lessons: Array<{
      id: string;
      title: string;
      summary: string | null;
      optional: boolean;
      estimatedMinutes: number | null;
      completed: boolean;
    }>;
  }>;
}
export interface LessonRequirements {
  mediaLeft: string[];
  checksLeft: string[];
  canComplete: boolean;
}
/** An author's preview of a lesson draft, with answer keys for its knowledge checks. */
export interface LessonPreview {
  title: string;
  summary: string | null;
  optional: boolean;
  estimatedMinutes: number | null;
  blocks: LearnerBlock[];
  assets: Record<string, LearnerAsset>;
  answerKeys: Record<
    string,
    { correctOptionIds: string[]; explanation: string | null; skill: string }
  >;
}
export interface LearnerLesson {
  courseSlug: string;
  id: string;
  title: string;
  summary: string | null;
  optional: boolean;
  estimatedMinutes: number | null;
  blocks: LearnerBlock[];
  assets: Record<string, LearnerAsset>;
  progress: LessonProgressState;
  requirements: LessonRequirements;
  previousLessonId: string | null;
  nextLessonId: string | null;
}
/** A course or quiz in the catalog, with the learner's own progress. */
export interface CatalogItem {
  kind: 'course' | 'quiz' | 'event';
  id: string;
  slug: string;
  type: CatalogType;
  title: string;
  summary: string | null;
  from: string;
  fromLogoUrl: string | null;
  sourceId: string | null;
  level: Level;
  topics: Topic[];
  minutes: number | null;
  lessonCount: number | null;
  questionCount: number | null;
  learners: number;
  publishedAt: string;
  status: CatalogStatus;
  lastActiveAt: string | null;
  progress: { completed: number; required: number } | null;
  attempts: number;
  best: { attemptId: string; percent: number; passed: boolean } | null;
  /** Set when one of the learner's orgs assigned this course. */
  assignment: { from: string; dueAt: string | null; note: string | null } | null;
  /** Workshops and webinars. */
  event: {
    /** The next session that hasn't ended (or the last one, once it's over). */
    nextSession: {
      startsAt: string;
      endsAt: string;
      format: SessionFormat;
      venue: string | null;
    } | null;
    sessionCount: number;
    startsAt: string | null;
    endsAt: string | null;
    /** The event's own zone (times are entered and shown in it). */
    timeZone: string;
    registration: RegistrationStatus | null;
    seatsLeft: number | null;
  } | null;
}
export interface CatalogPage {
  items: CatalogItem[];
  total: number;
  /** Only on the first page: Nirvik plus the learner's orgs that publish something. */
  sources?: { id: string; name: string }[];
}
export interface HomeGroup {
  items: CatalogItem[];
  total: number;
}
export interface HomeFeed {
  assigned: CatalogItem[];
  /** Workshops and webinars the learner registered for, soonest first. */
  upcoming: HomeGroup;
  /** Upcoming workshops and webinars open to register. */
  openEvents: HomeGroup;
  inProgress: HomeGroup;
  completed: HomeGroup;
  new: HomeGroup;
}

export interface CourseAssignment {
  id: string;
  courseId: string;
  courseTitle: string;
  courseSlug: string;
  dueAt: string | null;
  note: string | null;
  createdAt: string;
  /** The org that assigned it. */
  assignedBy: string;
  /** Assigned by a parent org: shown here, changed there. */
  inherited: boolean;
  /** Aimed at these groups of the assigning org (ids and names); null for everyone. */
  groupIds: string[] | null;
  groups: string[] | null;
  learners: number;
  completed: number;
  inProgress: number;
  overdue: number;
}

export interface AssignmentLearner {
  userId: string;
  name: string | null;
  email: string;
  status: 'not_started' | 'in_progress' | 'completed';
  percent: number;
  enrolledAt: string | null;
  completedAt: string | null;
  lastActiveAt: string | null;
}

export interface CourseReport {
  courseId: string;
  title: string;
  slug: string;
  from: string;
  learners: number;
  completed: number;
  inProgress: number;
  averagePercent: number;
  certificates: number;
  /** Final assessment results; null without one or before any attempt. */
  assessment: { attempts: number; averagePercent: number; passRate: number } | null;
}

/** A person as their admins see them: platform admins everything, org admins their tree only. */
export interface UserProfile {
  user: {
    id: string;
    email: string;
    displayName: string | null;
    createdAt: string;
    timeZone: string | null;
    /** Platform admins only. */
    isPlatformAdmin?: boolean;
    suspendedAt?: string | null;
  };
  lastActiveAt: string | null;
  memberships: {
    orgId: string;
    orgName: string;
    role: Role;
    joinedAt: string;
    leftAt: string | null;
  }[];
  courses: {
    courseId: string;
    title: string;
    slug: string;
    enrolledAt: string;
    completedAt: string | null;
    lastActiveAt: string;
    percent: number;
  }[];
  quizzes: {
    quizId: string;
    title: string;
    courseAssessment: boolean;
    attempts: number;
    bestPercent: number | null;
    passed: boolean;
    lastAt: string;
  }[];
  certificates: {
    code: string;
    title: string;
    issuerName: string;
    percent: number;
    kind: 'quiz' | 'course' | 'event';
    isPublic: boolean;
    issuedAt: string;
    timeZone: string;
  }[];
  activity: {
    at: string;
    kind: 'enrolled' | 'completed_course' | 'quiz' | 'certificate' | 'joined';
    title: string;
    detail: string | null;
  }[];
  summary: {
    coursesEnrolled: number;
    coursesCompleted: number;
    quizzesTaken: number;
    certificates: number;
  };
}

export interface PlatformReport {
  totals: {
    users: number;
    newUsers30: number;
    activeLearners30: number;
    orgs: number;
    topLevelOrgs: number;
    publishedCourses: number;
    publishedQuizzes: number;
    enrollments: number;
    completions: number;
    quizAttempts: number;
    certificates: number;
  };
  weekly: { weekStart: string; signups: number; activeLearners: number }[];
  organizations: {
    id: string;
    name: string;
    planId: string;
    members: number;
    quizAttempts: number;
    courseEnrollments: number;
    courseCompletions: number;
  }[];
  courses: CourseReport[];
  quizzes: QuizReport[];
}

export interface PublicQuiz {
  slug: string;
  title: string;
  description: string | null;
  mode: QuizMode;
  questionCount: number;
  passPercent: number;
  certificate: boolean;
  maxAttempts: number | null;
  issuerName: string;
  offeredBy: OrgBrand | null;
  audience: Audience;
  audienceScope?: AudienceScope | null;
}
export interface StartedAttempt {
  attemptId: string;
  quiz: {
    title: string;
    description: string | null;
    mode: QuizMode;
    issuerName: string;
    passPercent: number;
  };
  /** An author's preview of a course assessment: scored, never recorded. */
  preview?: true;
  questions: TakerQuestion[];
}
/** Where a learner stands on a course's final assessment. */
export interface CourseAssessmentState {
  title: string;
  description: string | null;
  questionCount: number;
  passPercent: number;
  /** Every required lesson is complete. */
  unlocked: boolean;
  requiredLeft: number;
  attempted: boolean;
  attempts: number;
  passed: boolean;
  bestPercent: number | null;
  /** Set while the learner waits after too many failed attempts. */
  retryAt: string | null;
}
/** What a taker sees after finishing: score and skills, never questions or answers. */
export interface AttemptResult {
  quiz: {
    title: string;
    mode: QuizMode;
    passPercent: number;
    /** Null for course assessments, which aren't shared. */
    slug: string | null;
    audience: QuizAudience;
  };
  /** Set for a course's final assessment: results lead back to the course. */
  course: { slug: string; title: string; certificateCode: string | null } | null;
  preview?: true;
  result: QuizResult;
  certificateCode: string | null;
}
/** Self-assessment feedback: right or wrong; an explanation only when right. */
export interface AnswerFeedback {
  correct: boolean;
  explanation: string | null;
  skill: Skill;
}
export interface Certificate {
  id: string;
  code: string;
  title: string;
  recipientName: string;
  issuerName: string;
  percent: number;
  isPublic: boolean;
  issuedAt: string;
  /** The learner's zone at issue; the issue date is shown in it. */
  timeZone: string;
  courseId: string | null;
  /** Workshops and webinars: a certificate of participation. */
  eventId: string | null;
}
export interface QuizReport {
  quizId: string;
  title: string;
  /** Who made the quiz: this or another org, or Nirvik Ready. */
  from: string;
  certificates: number;
  attempts: number;
  learners: number;
  averagePercent: number;
  passRate: number;
  skills: Partial<Record<Skill, number>>;
}
/** Where authored content lives: an org's bank, or Nirvik's library. */
export type ContentOwner = { kind: 'org'; orgId: string } | { kind: 'library' };
// ─── Workshops and webinars ───────────────────────────────────────────

/** A session as sent to the API: `startsAt` as an ISO instant. */
export interface SessionInputBody {
  title?: string;
  startsAt: string;
  durationMinutes: number;
  format: SessionFormat;
  venue?: string;
  address?: string;
  meetingUrl?: string;
  meetingDetails?: string;
}

export type EventStatus = 'draft' | 'published' | 'cancelled' | 'archived';

/** A session as authors see it (with the meeting link and check-in code). */
export interface EventSession {
  id: string;
  eventId: string;
  title: string | null;
  startsAt: string;
  endsAt: string;
  format: SessionFormat;
  venue: string | null;
  address: string | null;
  meetingUrl: string | null;
  meetingDetails: string | null;
  /** Hosts show this during the session; attendees enter it to check in. */
  checkInCode: string;
}

export interface EventSummary {
  id: string;
  ownerOrgId: string | null;
  slug: string;
  kind: EventKind;
  title: string;
  summary: string | null;
  audience: Audience;
  audienceScope?: AudienceScope | null;
  status: EventStatus;
  timeZone: string;
  updatedAt: string;
  startsAt: string | null;
  sessionCount: number;
  registered: number;
  waitlisted: number;
}

/** The event as its authors edit it. */
export interface EventDraft {
  id: string;
  ownerOrgId: string | null;
  slug: string;
  kind: EventKind;
  title: string;
  summary: string | null;
  description: string | null;
  hosts: string | null;
  audience: Audience;
  audienceScope?: AudienceScope | null;
  timeZone: string;
  level: Level;
  topics: Topic[];
  tags: string[];
  coverAssetId: string | null;
  settings: EventSettings;
  status: EventStatus;
  publishedAt: string | null;
  cancelledAt: string | null;
  updatedAt: string;
  sessions: EventSession[];
  registered: number;
  waitlisted: number;
  reuse?: ReusePolicy | null;
  source: ContentSource | null;
}

/** One item in a feedback inbox; `sender` is null when hidden (anonymous, for org admins). */
export interface FeedbackItem {
  id: string;
  reaction: number | null;
  category: FeedbackCategory | null;
  message: string | null;
  pageUrl: string | null;
  anonymous: boolean;
  status: FeedbackStatus;
  createdAt: string;
  readAt: string | null;
  sender: { userId: string; name: string | null; email: string } | null;
}
export interface FeedbackSummary {
  unread: number;
  last30: number;
  averageReaction: number | null;
  previousAverage: number | null;
  categories: Record<FeedbackCategory, number>;
}
export interface HelpFile {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  status: string;
  url: string | null;
}
export interface HelpMessage {
  id: string;
  body: string;
  fromStaff: boolean;
  internal: boolean;
  createdAt: string;
  author: { userId: string | null; name: string };
  files: HelpFile[];
}
export interface HelpRequestDetail {
  id: string;
  subject: string;
  type: HelpType;
  status: HelpStatus;
  orgId: string | null;
  inboxName: string;
  movedFromOrgId: string | null;
  pageUrl: string | null;
  createdAt: string;
  resolvedAt: string | null;
  requester: { userId: string; name: string | null; email: string };
  assigneeId: string | null;
  /** requester | read (parent admins) | manage */
  access: 'requester' | 'read' | 'manage';
  isRequester: boolean;
  assignees: { userId: string; name: string }[];
  messages: HelpMessage[];
}
export interface MyHelpRequest {
  id: string;
  subject: string;
  type: HelpType;
  status: HelpStatus;
  inboxName: string;
  updatedAt: string;
  createdAt: string;
  unread: boolean;
}
export interface HelpInboxItem {
  id: string;
  subject: string;
  type: HelpType;
  status: HelpStatus;
  requester: string;
  assignee: string | null;
  lastRequesterAt: string;
  createdAt: string;
  unread: boolean;
  movedFromOrg: boolean;
}

/** Where an inbox lives: the platform's, or an org's. */
export type InboxScope = { kind: 'platform' } | { kind: 'org'; orgId: string };
const inboxBase = (s: InboxScope) => (s.kind === 'platform' ? '/v1/admin' : `/v1/orgs/${s.orgId}`);

/** Something an org (or Nirvik's library) can start new content from. */
export interface ReusableItem {
  id: string;
  kind: ReuseKind;
  slug: string;
  title: string;
  summary: string | null;
  ownerName: string;
  fromLibrary: boolean;
  /** The target's own content (duplicate) rather than someone else's. */
  own: boolean;
  status: string;
  audience: string;
  latestVersion: number | null;
  canFork: boolean;
  canCustomize: boolean;
}

export interface EventAttendees {
  sessions: { id: string; title: string | null; startsAt: string; checkInCode: string }[];
  attendees: {
    userId: string;
    email: string;
    displayName: string | null;
    status: RegistrationStatus;
    registeredAt: string | null;
    queuedAt: string;
    /** Session ids attended. */
    attended: string[];
    certificate: boolean;
  }[];
  capacity: number | null;
}

/** What anyone who may see the event gets (no meeting links or codes). */
export interface LibraryEvent {
  slug: string;
  kind: EventKind;
  title: string;
  summary: string | null;
  hosts: string | null;
  /** The org running it, or Nirvik Ready. */
  hostName: string;
  host: OrgBrand | null;
  level: Level;
  timeZone: string;
  startsAt: string;
  endsAt: string;
  /** The next session that hasn't ended. */
  nextStartsAt: string;
  sessionCount: number;
  formats: SessionFormat[];
  certificate: boolean;
  seatsLeft: number | null;
}

export interface PublicEvent {
  slug: string;
  kind: EventKind;
  title: string;
  summary: string | null;
  /** Markdown. */
  description: string | null;
  hosts: string | null;
  /** The org running it, or "Nirvik Ready". */
  hostName: string;
  ownerOrgId: string | null;
  offeredBy: OrgBrand | null;
  audience: Audience;
  audienceScope?: AudienceScope | null;
  timeZone: string;
  level: Level;
  topics: Topic[];
  tags: string[];
  certificate: boolean;
  /** Sessions needed for the certificate; null means all. */
  minSessions: number | null;
  capacity: number | null;
  seatsLeft: number | null;
  status: EventStatus;
  cancelled: boolean;
  sessions: {
    id: string;
    title: string | null;
    startsAt: string;
    endsAt: string;
    format: SessionFormat;
    venue: string | null;
    address: string | null;
  }[];
  startsAt: string | null;
  endsAt: string | null;
  /** Until the last session starts. */
  registrationOpen: boolean;
}

/** The signed-in learner's side of an event. */
export interface MyEventState {
  status: RegistrationStatus | null;
  waitlistPosition: number | null;
  /** Certificate events need a name on file before registering. */
  needsName: boolean;
  /** Same order as PublicEvent.sessions. */
  sessions: {
    id: string;
    /** Registered attendees only. */
    meetingUrl: string | null;
    meetingDetails: string | null;
    /** From 30 minutes before until the end (online and hybrid). */
    joinOpen: boolean;
    /** From 15 minutes before until 30 minutes after. */
    checkInOpen: boolean;
    attended: boolean;
    googleCalendarUrl: string | null;
  }[];
  /** Private calendar (subscribe or download), registered attendees only. */
  calendarUrl: string | null;
  certificateCode: string | null;
}

const contentBase = (o: ContentOwner) =>
  o.kind === 'org' ? `/v1/orgs/${o.orgId}` : '/v1/admin/library';

export interface AuditEntry {
  log: {
    id: string;
    action: string;
    targetType: string | null;
    targetId: string | null;
    details: unknown;
    createdAt: string;
  };
  actorEmail: string | null;
  actorName: string | null;
  orgName: string | null;
}

export interface Org {
  id: string;
  name: string;
  type: OrgType;
  parentId: string | null;
  depth: number;
  country: string;
  joinPolicy: 'open' | 'approval' | 'invite_only' | 'managed';
  slug: string;
  logoUrl?: string | null;
  verifiedAt?: string | null;
  profileHiddenAt?: string | null;
  profilePublic?: boolean;
}
export interface MyOrg extends Pick<Org, 'id' | 'name' | 'type' | 'parentId' | 'depth' | 'slug'> {
  role: Role;
  logoUrl: string | null;
}

/** Settings → Profile. */
export interface OrgProfileSettings {
  slug: string;
  tagline: string | null;
  about: string | null;
  websiteUrl: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  city: string | null;
  socialLinks: OrgSocialLinks;
  profilePublic: boolean;
  contactPublic: boolean;
  logoUrl: string | null;
  verified: boolean;
  hidden: boolean;
}

/** Something an org offers to everyone, on its profile. */
export interface OrgOffering {
  kind: 'course' | 'quiz' | 'event';
  slug: string;
  type: CatalogType;
  title: string;
  summary: string | null;
  level: Level;
  minutes: number | null;
  lessonCount: number | null;
  questionCount: number | null;
  startsAt: string | null;
  endsAt: string | null;
}

export interface OrgProfile extends OrgBrand {
  type: OrgType;
  country: string;
  city: string | null;
  tagline: string | null;
  about: string | null;
  websiteUrl: string | null;
  socialLinks: OrgSocialLinks;
  contactEmail: string | null;
  contactPhone: string | null;
  contactForMembers: boolean;
  parent: OrgBrand | null;
  children: OrgBrand[];
  isMember: boolean;
  hidden: boolean;
  offerings: OrgOffering[];
}

export interface DirectoryOrg extends OrgBrand {
  type: OrgType;
  country: string;
  city: string | null;
  tagline: string | null;
  offerings: number;
}
export interface Member {
  userId: string;
  email: string;
  displayName: string | null;
  orgId: string;
  orgName: string;
  role: Role;
  source: string;
  joinedAt: string;
  /** The member's groups in `orgId`. */
  groups: string[];
  /** Engagement in this org's tree (see services/members.ts). */
  coursesStarted: number;
  coursesCompleted: number;
  quizzesTaken: number;
  quizzesPassed: number;
  eventsAttended: number;
  certificates: number;
  lastActiveAt: string | null;
  activity: MemberActivity;
}
export interface MemberSummary {
  members: number;
  active: number;
  quiet: number;
  inactive: number;
  coursesCompleted: number;
  quizzesPassed: number;
  eventsAttended: number;
  certificates: number;
}
export interface OrgDomain {
  id: string;
  domain: string;
  verifiedAt: string | null;
  record: { name: string; value: string };
}
export interface Group {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  /** Active members of the org in this group. */
  members: number;
}
export interface Invitation {
  id: string;
  email: string;
  role: Role;
  groupId: string | null;
  groupName: string | null;
  invitedBy: string | null;
  lastSentAt: string | null;
  expiresAt: string;
  createdAt: string;
  expired: boolean;
}
/** Someone waiting to join after a domain sign-in (approval policy or no free seat). */
export interface JoinRequest {
  id: string;
  userId: string;
  email: string;
  displayName: string | null;
  reason: JoinRequestReason;
  createdAt: string;
}
/** What the invitation page shows to whoever holds the link. */
export interface InvitationDetails {
  orgId: string;
  orgName: string;
  orgType: string;
  email: string;
  role: Role;
  groupName: string | null;
  inviterName: string | null;
  message: string | null;
  expiresAt: string;
  status: 'open' | 'accepted' | 'expired' | 'revoked';
}
export interface InviteCode {
  id: string;
  code: string;
  role: Role;
  groupId: string | null;
  maxUses: number | null;
  uses: number;
  expiresAt: string | null;
  createdAt: string;
}

export type CertificateFileFormat = 'pdf' | 'png';

/** A public certificate's PDF or PNG (plain link; no account needed). */
export const publicCertificateFileUrl = (code: string, format: CertificateFileFormat) =>
  `${BASE}/v1/public/certificates/${code}/${format}`;

/** Its public verification page. */
export const certificateVerifyUrl = (code: string) => `${window.location.origin}/verify/${code}`;

/** A quiz's public page, for sharing. */
export const quizUrl = (slug: string) => `${window.location.origin}/q/${slug}`;

export const courseUrl = (slug: string) => `${window.location.origin}/c/${slug}`;

/** On the verify page: the course, quiz or upcoming event a visitor can take to earn their own certificate. */
export interface EarnYourOwn {
  kind: 'quiz' | 'course' | 'event';
  slug: string;
  title: string;
  minutes: number | null;
}

export const pathUrl = (path: string) => `${window.location.origin}/paths/${path}`;

/** A learning path's cover (its artwork on the brand background). */
export const pathCoverUrl = (path: string) => `${BASE}/v1/public/paths/${path}/cover.png`;

/**
 * LinkedIn's "Add licenses & certifications" form, pre-filled with this
 * certificate. The issuing organization is always Nirvik AI Labs, the legal
 * entity behind every certificate (also on its seal), even for org quizzes.
 */
export function linkedInAddToProfileUrl(
  c: Pick<Certificate, 'code' | 'title' | 'issuedAt' | 'timeZone'>,
): string {
  const issued = yearMonthIn(c.issuedAt, c.timeZone);
  return `https://www.linkedin.com/profile/add?${new URLSearchParams({
    startTask: 'CERTIFICATION_NAME',
    name: c.title,
    organizationName: 'Nirvik AI Labs',
    issueYear: String(issued.year),
    issueMonth: String(issued.month),
    certUrl: certificateVerifyUrl(c.code),
    certId: c.code,
  })}`;
}

export type PosterTheme = 'light' | 'dark';
export type PaperSize = 'letter' | 'a4';

/** Saves a downloaded file under the given name. */
/** `?a=1&b=2` from the defined, non-empty values (or nothing). */
export function toQueryString(params: Record<string, string | number | undefined | null>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params))
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** API client bound to the current session's auth headers. */
export function useApi() {
  const { authHeaders } = useAuth();

  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await fetch(`${BASE}${path}`, {
      method,
      headers: {
        ...(await authHeaders()),
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as {
        error?: string | { issues?: { message?: string; path?: (string | number)[] }[] };
        limit?: LimitKey;
      };
      throw new ApiError(errorMessage(data.error, res.status), res.status, data.limit);
    }
    // 204 No Content (e.g. deletes) has no body.
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  /** A binary response (PDF, PNG), fetched with the session's auth headers. */
  async function file(path: string): Promise<Blob> {
    const res = await fetch(`${BASE}${path}`, { headers: await authHeaders() });
    if (!res.ok) throw new ApiError(`Download failed (${res.status})`, res.status);
    return res.blob();
  }

  return {
    me: () => request<Me>('GET', '/v1/me'),
    setTimeZone: (timeZone: string) =>
      request<{ timeZone: string }>('PUT', '/v1/me/time-zone', { timeZone }),
    updateMe: (displayName: string) =>
      request<{ id: string; displayName: string } & NameStatus>('PATCH', '/v1/me', { displayName }),
    home: () => request<HomeFeed>('GET', '/v1/me/home'),
    catalog: (query: Partial<CatalogQuery>) =>
      request<CatalogPage>('GET', `/v1/catalog${toQueryString(query)}`),
    certificates: () => request<{ certificates: Certificate[] }>('GET', '/v1/me/certificates'),
    /** Downloads one of your certificates as a PDF or PNG, public or private. */
    downloadCertificate: async (code: string, format: CertificateFileFormat) =>
      saveBlob(
        await file(`/v1/me/certificates/${code}/${format}`),
        `Nirvik-Ready-Certificate-${code}.${format}`,
      ),
    /** A quiz's campaign poster (org quizzes need membership, hence the auth headers). */
    // Workshops and webinars: authoring (authors of the owner org; library: platform admins).
    events: (o: ContentOwner, q?: string) =>
      request<{ events: EventSummary[]; total: number }>(
        'GET',
        `${contentBase(o)}/events${toQueryString({ q })}`,
      ),
    createEvent: (
      o: ContentOwner,
      input: Partial<EventInput> & { title: string; timeZone: string },
    ) => request<EventDraft>('POST', `${contentBase(o)}/events`, input),
    event: (id: string) => request<EventDraft>('GET', `/v1/events/${id}`),
    updateEvent: (id: string, input: EventInput) =>
      request<EventDraft>('PUT', `/v1/events/${id}`, input),
    addEventSession: (eventId: string, input: SessionInputBody) =>
      request<EventSession>('POST', `/v1/events/${eventId}/sessions`, input),
    updateEventSession: (sessionId: string, input: SessionInputBody) =>
      request<EventSession>('PUT', `/v1/event-sessions/${sessionId}`, input),
    deleteEventSession: (sessionId: string) =>
      request<{ ok: true }>('DELETE', `/v1/event-sessions/${sessionId}`),
    publishEvent: (id: string) => request<EventDraft>('POST', `/v1/events/${id}/publish`),
    cancelEvent: (id: string, note?: string) =>
      request<EventDraft>('POST', `/v1/events/${id}/cancel`, { note }),
    archiveEvent: (id: string, archived: boolean) =>
      request<EventDraft>('POST', `/v1/events/${id}/archive`, { archived }),
    eventAttendees: (id: string) => request<EventAttendees>('GET', `/v1/events/${id}/attendees`),
    markAttendance: (sessionId: string, userId: string, attended: boolean) =>
      request<{ ok: true }>('PUT', `/v1/event-sessions/${sessionId}/attendance`, {
        userId,
        attended,
      }),
    // Workshops and webinars: learners. Details are public (org events need membership).
    publicEvent: (slug: string) => request<PublicEvent>('GET', `/v1/public/events/${slug}`),
    /** Upcoming public workshops and webinars, Nirvik's and orgs', soonest first (no sign-in; the landing page). */
    libraryEvents: () => request<{ events: LibraryEvent[] }>('GET', '/v1/public/library/events'),
    publicPath: (path: LearningPath) =>
      request<{ path: LearningPath; items: PublicPathItem[]; events: LibraryEvent[] }>(
        'GET',
        `/v1/public/paths/${path}`,
      ),
    myPath: (path?: LearningPath) =>
      request<{ path: LearningPath | null; items: CatalogItem[]; next: string | null }>(
        'GET',
        `/v1/me/path${path ? `?path=${path}` : ''}`,
      ),
    setPath: (path: LearningPath | null) =>
      request<{ learningPath: LearningPath | null }>('PUT', '/v1/me/path', { path }),
    /** Nirvik's public library courses, newest first (no sign-in; the landing page). */
    libraryCourses: () =>
      request<{
        courses: {
          slug: string;
          title: string;
          summary: string | null;
          level: Level;
          topics: Topic[];
          minutes: number | null;
          lessonCount: number | null;
          publishedAt: string;
        }[];
      }>('GET', '/v1/public/library/courses'),
    myEvent: (slug: string) => request<MyEventState>('GET', `/v1/learn/events/${slug}`),
    registerForEvent: (slug: string) =>
      request<{ status: RegistrationStatus }>('POST', `/v1/learn/events/${slug}/registration`, {
        timeZone: browserTimeZone(),
      }),
    cancelEventRegistration: (slug: string) =>
      request<{ ok: true }>('DELETE', `/v1/learn/events/${slug}/registration`),
    checkInToSession: (slug: string, sessionId: string, code: string) =>
      request<{ attended: true; certificateCode: string | null }>(
        'POST',
        `/v1/learn/events/${slug}/sessions/${sessionId}/check-in`,
        { code },
      ),
    posterFile: (
      slug: string,
      theme: PosterTheme,
      format: 'pdf' | 'png',
      size: PaperSize = 'letter',
      kind: 'quiz' | 'course' | 'event' | 'path' = 'quiz',
    ) =>
      file(
        `/v1/public/${{ quiz: 'quizzes', course: 'courses', event: 'events', path: 'paths' }[kind]}/${slug}/poster/${theme}/${format}?size=${size}`,
      ),
    setCertificatePublic: (id: string, isPublic: boolean) =>
      request<Certificate>('PATCH', `/v1/me/certificates/${id}`, { isPublic }),
    // Taking quizzes: the details are public; starting needs an account.
    publicQuiz: (slug: string) => request<PublicQuiz>('GET', `/v1/public/quizzes/${slug}`),
    startAttempt: (slug: string) => request<StartedAttempt>('POST', '/v1/attempts', { slug }),
    checkAnswer: (attemptId: string, answer: Answer) =>
      request<AnswerFeedback>('POST', `/v1/attempts/${attemptId}/check`, answer),
    submitAttempt: (attemptId: string, answers: Answer[]) =>
      request<{ result: QuizResult; certificateCode: string | null; preview?: true }>(
        'POST',
        `/v1/attempts/${attemptId}/submit`,
        // The zone fixes the issue date on any certificate this earns.
        { answers, timeZone: browserTimeZone() },
      ),
    attempt: (attemptId: string) => request<AttemptResult>('GET', `/v1/attempts/${attemptId}`),
    verifyCertificate: (code: string) =>
      request<
        Pick<
          Certificate,
          'code' | 'title' | 'recipientName' | 'issuerName' | 'percent' | 'issuedAt' | 'timeZone'
        > & {
          kind: 'quiz' | 'course' | 'event';
          offeredBy: OrgBrand | null;
          earn: EarnYourOwn | null;
        }
      >('GET', `/v1/public/certificates/${code}`),
    // Courses: authoring.
    /** Up to 200, newest first; `q` searches titles. */
    courses: (o: ContentOwner, q?: string) =>
      request<{ courses: CourseSummary[]; total: number }>(
        'GET',
        `${contentBase(o)}/courses${toQueryString({ q })}`,
      ),
    createCourse: (o: ContentOwner, input: Partial<CourseInput> & { title: string }) =>
      request<Course>('POST', `${contentBase(o)}/courses`, input),
    courseDraft: (id: string) => request<CourseDraft>('GET', `/v1/courses/${id}`),
    updateCourse: (id: string, input: CourseInput) =>
      request<Course>('PUT', `/v1/courses/${id}`, input),
    updateOutline: (id: string, outline: CourseOutline) =>
      request<{ outline: CourseOutline }>('PUT', `/v1/courses/${id}/outline`, outline),
    createLesson: (courseId: string, moduleId: string, lesson: LessonInput, moduleTitle?: string) =>
      request<DraftLesson>('POST', `/v1/courses/${courseId}/lessons`, {
        moduleId,
        moduleTitle,
        lesson,
      }),
    previewLesson: (id: string, lesson: LessonInput) =>
      request<LessonPreview>('POST', `/v1/course-lessons/${id}/preview`, lesson),
    lessonDraft: (id: string) => request<DraftLesson>('GET', `/v1/course-lessons/${id}`),
    updateLesson: (id: string, lesson: LessonInput) =>
      request<DraftLesson>('PUT', `/v1/course-lessons/${id}`, lesson),
    deleteLesson: (id: string) => request<void>('DELETE', `/v1/course-lessons/${id}`),
    /** A customized course's own title and/or note for a source lesson (null clears). */
    changeSourceLesson: (
      courseId: string,
      lessonId: string,
      change: { title?: string | null; note?: string | null },
    ) => request<{ ok: true }>('PUT', `/v1/courses/${courseId}/source-lessons/${lessonId}`, change),
    publishCourse: (id: string) =>
      request<{ version: number }>('POST', `/v1/courses/${id}/publish`),
    archiveCourse: (id: string, archived: boolean) =>
      request<{ status: string }>('POST', `/v1/courses/${id}/archive`, { archived }),
    cloneCourse: (id: string, ownerOrgId: string | null) =>
      request<Course>('POST', `/v1/courses/${id}/clone`, { ownerOrgId }),
    /** Reads a page's title, description and image for a link block. */
    linkPreview: (url: string) =>
      request<{
        title: string | null;
        description: string | null;
        imageUrl: string | null;
        siteName: string | null;
      }>('GET', `/v1/link-preview?url=${encodeURIComponent(url)}`),
    // AI assist: drafts to review before saving.
    aiStatus: () => request<{ enabled: boolean }>('GET', '/v1/ai/status'),
    startAiJob: (input: AiJobRequest) => request<AiJob>('POST', '/v1/ai/jobs', input),
    aiJob: (id: string) => request<AiJob>('GET', `/v1/ai/jobs/${id}`),
    appendOutline: (courseId: string, input: ApplyOutline) =>
      request<Course>('POST', `/v1/courses/${courseId}/outline/append`, input),
    // Course files.
    courseFiles: (o: ContentOwner, courseId?: string) =>
      request<{ files: CourseFile[] }>(
        'GET',
        `${contentBase(o)}/course-files${courseId ? `?courseId=${courseId}` : ''}`,
      ),
    requestUpload: (
      o: ContentOwner,
      input: { filename: string; contentType: string; sizeBytes: number; courseId?: string | null },
    ) =>
      request<{
        asset: CourseFile;
        upload: { url: string; method: 'PUT'; headers: Record<string, string> };
      }>('POST', `${contentBase(o)}/course-files`, input),
    completeUpload: (id: string) => request<CourseFile>('POST', `/v1/course-files/${id}/complete`),
    courseFile: (id: string) => request<CourseFile>('GET', `/v1/course-files/${id}`),
    deleteCourseFile: (id: string) => request<void>('DELETE', `/v1/course-files/${id}`),
    // Courses: learners.
    publicCourse: (slug: string) => request<PublicCourse>('GET', `/v1/public/courses/${slug}`),
    learnerCourse: (slug: string) =>
      request<LearnerCourse | null>('GET', `/v1/learn/courses/${slug}`),
    enroll: (slug: string) => request<LearnerCourse>('POST', `/v1/learn/courses/${slug}/enroll`),
    upgradeCourse: (slug: string) =>
      request<LearnerCourse>('POST', `/v1/learn/courses/${slug}/upgrade`, {
        timeZone: browserTimeZone(),
      }),
    courseAssessment: (slug: string) =>
      request<CourseAssessmentState & { course: { slug: string; title: string } }>(
        'GET',
        `/v1/learn/courses/${slug}/assessment`,
      ),
    startCourseAssessment: (slug: string, preview = false) =>
      request<StartedAttempt>('POST', `/v1/learn/courses/${slug}/assessment/attempts`, {
        preview,
      }),
    learnerLesson: (slug: string, lessonId: string) =>
      request<LearnerLesson>('GET', `/v1/learn/courses/${slug}/lessons/${lessonId}`),
    lessonMedia: (slug: string, lessonId: string, media: Record<string, number>) =>
      request<LessonRequirements>('PUT', `/v1/learn/courses/${slug}/lessons/${lessonId}/progress`, {
        media,
      }),
    lessonCheck: (
      slug: string,
      lessonId: string,
      answer: { blockId: string; questionId: string; optionIds: string[] },
    ) =>
      request<{
        correct: boolean;
        explanation: string | null;
        skill: string;
        requirements: LessonRequirements;
      }>('POST', `/v1/learn/courses/${slug}/lessons/${lessonId}/check`, answer),
    completeLesson: (slug: string, lessonId: string) =>
      request<LearnerCourse>('POST', `/v1/learn/courses/${slug}/lessons/${lessonId}/complete`, {
        timeZone: browserTimeZone(),
      }),
    // Quizzes: authoring.
    banks: (o: ContentOwner) =>
      request<{ banks: QuestionBankSummary[] }>('GET', `${contentBase(o)}/question-banks`),
    createBank: (o: ContentOwner, input: QuestionBankInput) =>
      request<QuestionBank>('POST', `${contentBase(o)}/question-banks`, input),
    bank: (id: string, q = '') =>
      request<{ bank: QuestionBank; questions: Question[] }>(
        'GET',
        `/v1/question-banks/${id}${q ? `?q=${encodeURIComponent(q)}` : ''}`,
      ),
    updateBank: (id: string, input: QuestionBankInput) =>
      request<QuestionBank>('PUT', `/v1/question-banks/${id}`, input),
    archiveBank: (id: string) => request<{ ok: true }>('DELETE', `/v1/question-banks/${id}`),
    createQuestion: (bankId: string, input: QuestionInput) =>
      request<Question>('POST', `/v1/question-banks/${bankId}/questions`, input),
    createQuestions: (bankId: string, questions: QuestionInput[]) =>
      request<{ questions: Question[] }>('POST', `/v1/question-banks/${bankId}/questions/bulk`, {
        questions,
      }),
    question: (id: string) => request<Question>('GET', `/v1/questions/${id}`),
    updateQuestion: (id: string, input: QuestionInput) =>
      request<Question>('PUT', `/v1/questions/${id}`, input),
    archiveQuestion: (id: string) => request<{ ok: true }>('DELETE', `/v1/questions/${id}`),
    /** Up to 200, newest first; `q` searches titles, `audience: 'course'` lists course assessments. */
    quizzes: (o: ContentOwner, filters: { q?: string; audience?: QuizAudience } = {}) =>
      request<{ quizzes: Quiz[]; total: number }>(
        'GET',
        `${contentBase(o)}/quizzes${toQueryString(filters)}`,
      ),
    createQuiz: (o: ContentOwner, input: Partial<QuizInput> & { title: string }) =>
      request<Quiz>('POST', `${contentBase(o)}/quizzes`, input),
    quiz: (id: string) =>
      request<
        Quiz & {
          questions: QuizQuestionRef[];
          source: ContentSource | null;
          overlay: QuizOverlayView | null;
        }
      >('GET', `/v1/quizzes/${id}`),
    /** What `o` can start new content from: Nirvik's library, its own and its parents'. */
    reusable: (o: ContentOwner, kind: ReuseKind, q = '') =>
      request<{ items: ReusableItem[] }>(
        'GET',
        `/v1/reuse${toQueryString({ kind, q: q || undefined, ownerOrgId: o.kind === 'org' ? o.orgId : undefined })}`,
      ),
    /** A customized version of a quiz for `o` that keeps following it. */
    customize: (kind: 'course' | 'quiz', id: string, o: ContentOwner) =>
      request<{ id: string }>(
        'POST',
        `/v1/${kind === 'course' ? 'courses' : 'quizzes'}/${id}/customize`,
        {
          ownerOrgId: o.kind === 'org' ? o.orgId : null,
        },
      ),
    /** A draft copy owned by `o`; returns the new item's id. */
    fork: (kind: ReuseKind, id: string, o: ContentOwner) =>
      request<{ id: string }>(
        'POST',
        `/v1/${kind === 'course' ? 'courses' : kind === 'quiz' ? 'quizzes' : 'events'}/${id}/${kind === 'event' ? 'duplicate' : 'fork'}`,
        { ownerOrgId: o.kind === 'org' ? o.orgId : null },
      ),
    updateQuiz: (id: string, input: QuizInput) => request<Quiz>('PUT', `/v1/quizzes/${id}`, input),
    publishQuiz: (id: string) => request<{ version: number }>('POST', `/v1/quizzes/${id}/publish`),
    archiveQuiz: (id: string, archived: boolean) =>
      request<Quiz>('POST', `/v1/quizzes/${id}/archive`, { archived }),
    courseReport: (orgId: string) =>
      request<{ courses: CourseReport[] }>('GET', `/v1/orgs/${orgId}/reports/courses`),
    assignments: (orgId: string) =>
      request<{ assignments: CourseAssignment[] }>('GET', `/v1/orgs/${orgId}/course-assignments`),
    assignableCourses: (orgId: string) =>
      request<{ courses: { id: string; slug: string; title: string; fromLibrary: boolean }[] }>(
        'GET',
        `/v1/orgs/${orgId}/course-assignments/courses`,
      ),
    assignCourse: (
      orgId: string,
      input: {
        courseId: string;
        dueAt: string | null;
        note?: string | null;
        groupIds?: string[] | null;
      },
    ) => request<{ id: string }>('POST', `/v1/orgs/${orgId}/course-assignments`, input),
    updateAssignment: (
      orgId: string,
      id: string,
      input: { dueAt: string | null; note?: string | null; groupIds?: string[] | null },
    ) => request<{ id: string }>('PATCH', `/v1/orgs/${orgId}/course-assignments/${id}`, input),
    deleteAssignment: (orgId: string, id: string) =>
      request<{ ok: true }>('DELETE', `/v1/orgs/${orgId}/course-assignments/${id}`),
    assignmentLearners: (orgId: string, id: string) =>
      request<{ learners: AssignmentLearner[] }>(
        'GET',
        `/v1/orgs/${orgId}/course-assignments/${id}/learners`,
      ),
    quizReport: (orgId: string) =>
      request<{ quizzes: QuizReport[] }>('GET', `/v1/orgs/${orgId}/reports/quizzes`),
    myOrgRequests: () => request<{ requests: OrgRequest[] }>('GET', '/v1/me/org-requests'),
    requestOrg: (input: OrgRequestInput) =>
      request<OrgRequest>('POST', '/v1/me/org-requests', input),
    cancelOrgRequest: (id: string) =>
      request<{ ok: true }>('POST', `/v1/me/org-requests/${id}/cancel`),
    usage: (orgId: string) => request<TreeUsage>('GET', `/v1/orgs/${orgId}/usage`),
    myOrgs: () => request<{ orgs: MyOrg[] }>('GET', '/v1/me/orgs'),
    createOrg: (input: CreateOrgInput) => request<Org>('POST', '/v1/orgs', input),
    org: (orgId: string) => request<Org>('GET', `/v1/orgs/${orgId}`),
    updateOrg: (orgId: string, input: Partial<Pick<Org, 'name' | 'joinPolicy'>>) =>
      request<Org>('PATCH', `/v1/orgs/${orgId}`, input),
    children: (orgId: string) => request<{ orgs: Org[] }>('GET', `/v1/orgs/${orgId}/children`),
    orgProfileSettings: (orgId: string) =>
      request<OrgProfileSettings>('GET', `/v1/orgs/${orgId}/profile`),
    updateOrgProfile: (orgId: string, input: OrgProfileInput) =>
      request<OrgProfileSettings>('PUT', `/v1/orgs/${orgId}/profile`, input),
    /** A square PNG the browser cropped. */
    uploadOrgLogo: async (orgId: string, png: Blob) => {
      const res = await fetch(`${BASE}/v1/orgs/${orgId}/logo`, {
        method: 'PUT',
        headers: { ...(await authHeaders()), 'content-type': 'image/png' },
        body: png,
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        throw new ApiError(errorMessage(data.error, res.status), res.status);
      }
      return (await res.json()) as { logoUrl: string };
    },
    removeOrgLogo: (orgId: string) => request<void>('DELETE', `/v1/orgs/${orgId}/logo`),
    /** /o/:slug: public profiles, or private ones for people in the org's tree. */
    orgProfile: (slug: string) =>
      request<{ profile: OrgProfile } | { redirect: string }>('GET', `/v1/public/orgs/${slug}`),
    orgDirectory: (query: Partial<OrgDirectoryQuery>) =>
      request<{ orgs: DirectoryOrg[]; total: number; countries: string[] | null }>(
        'GET',
        `/v1/public/orgs${toQueryString(query)}`,
      ),
    createChild: (orgId: string, input: CreateOrgInput) =>
      request<Org>('POST', `/v1/orgs/${orgId}/children`, input),
    memberProfile: (orgId: string, userId: string) =>
      request<UserProfile>('GET', `/v1/orgs/${orgId}/members/${userId}/profile`),
    /** A page of members with engagement (admins of the org or a parent org). */
    members: (
      orgId: string,
      query: Partial<Record<keyof MemberQuery, string | number | boolean>>,
    ) =>
      request<{ members: Member[]; total: number }>(
        'GET',
        `/v1/orgs/${orgId}/members${toQueryString(
          Object.fromEntries(
            Object.entries(query).map(([k, v]) => [k, v === undefined ? undefined : String(v)]),
          ),
        )}`,
      ),
    memberSummary: (orgId: string, descendants: boolean) =>
      request<MemberSummary>(
        'GET',
        `/v1/orgs/${orgId}/members-summary${descendants ? '?descendants=true' : ''}`,
      ),
    domains: (orgId: string) =>
      request<{ domains: OrgDomain[] }>('GET', `/v1/orgs/${orgId}/domains`),
    addDomain: (orgId: string, domain: string) =>
      request<OrgDomain>('POST', `/v1/orgs/${orgId}/domains`, { domain }),
    verifyDomain: (orgId: string, domainId: string) =>
      request<{ verified: boolean }>('POST', `/v1/orgs/${orgId}/domains/${domainId}/verify`),
    inviteCodes: (orgId: string) =>
      request<{ codes: InviteCode[] }>('GET', `/v1/orgs/${orgId}/invite-codes`),
    /** A poster inviting people to join with one of the org's codes. */
    invitePosterFile: (
      orgId: string,
      codeId: string,
      theme: PosterTheme,
      format: 'pdf' | 'png',
      size: PaperSize = 'letter',
    ) => file(`/v1/orgs/${orgId}/invite-codes/${codeId}/poster/${theme}/${format}?size=${size}`),
    revokeInviteCode: (orgId: string, codeId: string) =>
      request<{ ok: true }>('DELETE', `/v1/orgs/${orgId}/invite-codes/${codeId}`),
    createInviteCode: (orgId: string, input: Partial<CreateInviteCodeInput>) =>
      request<InviteCode>('POST', `/v1/orgs/${orgId}/invite-codes`, input),
    groups: (orgId: string) => request<{ groups: Group[] }>('GET', `/v1/orgs/${orgId}/groups`),
    sendFeedback: (input: Partial<FeedbackInput>) =>
      request<{ id: string }>('POST', '/v1/feedback', input),
    feedback: (scope: InboxScope, query: Partial<FeedbackQuery>) =>
      request<{ items: FeedbackItem[]; total: number }>(
        'GET',
        `${inboxBase(scope)}/feedback${toQueryString(query)}`,
      ),
    feedbackSummary: (scope: InboxScope) =>
      request<FeedbackSummary>('GET', `${inboxBase(scope)}/feedback/summary`),
    setFeedbackStatus: (scope: InboxScope, ids: string[], status: FeedbackStatus) =>
      request<{ updated: number }>('POST', `${inboxBase(scope)}/feedback/status`, { ids, status }),
    /** Unread items in the inboxes the user looks after (sidebar badges). */
    myHelpRequests: () => request<{ requests: MyHelpRequest[] }>('GET', '/v1/help'),
    helpRequest: (id: string) => request<HelpRequestDetail>('GET', `/v1/help/${id}`),
    createHelpRequest: (input: Partial<HelpRequestInput>) =>
      request<{ id: string }>('POST', '/v1/help', input),
    replyToHelp: (id: string, input: { body: string; internal?: boolean; fileIds?: string[] }) =>
      request<{ ok: true }>('POST', `/v1/help/${id}/messages`, input),
    updateHelp: (id: string, input: { status?: HelpStatus; assigneeId?: string | null }) =>
      request<{ ok: true }>('PATCH', `/v1/help/${id}`, input),
    moveHelpToPlatform: (id: string) =>
      request<{ ok: true }>('POST', `/v1/help/${id}/move-to-platform`),
    helpInbox: (scope: InboxScope, query: Partial<HelpQuery>) =>
      request<{ items: HelpInboxItem[]; total: number }>(
        'GET',
        `${inboxBase(scope)}/help${toQueryString(query)}`,
      ),
    requestHelpUpload: (input: { filename: string; contentType: string; sizeBytes: number }) =>
      request<{
        file: { id: string; filename: string; status: string };
        upload: { url: string; method: 'PUT'; headers: Record<string, string> };
      }>('POST', '/v1/help/files', input),
    completeHelpUpload: (id: string) =>
      request<{ id: string; status: string }>('POST', `/v1/help/files/${id}/complete`),
    inboxCounts: (orgId?: string) =>
      request<{
        platform?: { feedback: number; help: number };
        org?: { feedback: number; help: number };
        myHelp: number;
      }>('GET', `/v1/me/inbox-counts${toQueryString({ orgId })}`),
    /** Names of the org's groups and child orgs, for "Who can see this" (authors). */
    audienceOptions: (orgId: string) =>
      request<{
        groups: { id: string; name: string }[];
        children: { id: string; name: string; depth: number }[];
      }>('GET', `/v1/orgs/${orgId}/audience-options`),
    createGroup: (orgId: string, input: GroupInput) =>
      request<Group>('POST', `/v1/orgs/${orgId}/groups`, input),
    updateGroup: (orgId: string, groupId: string, input: GroupInput) =>
      request<Group>('PUT', `/v1/orgs/${orgId}/groups/${groupId}`, input),
    deleteGroup: (orgId: string, groupId: string) =>
      request<{ ok: true }>('DELETE', `/v1/orgs/${orgId}/groups/${groupId}`),
    addGroupMembers: (orgId: string, groupId: string, userIds: string[]) =>
      request<{ added: number }>('POST', `/v1/orgs/${orgId}/groups/${groupId}/members`, {
        userIds,
      }),
    removeGroupMember: (orgId: string, groupId: string, userId: string) =>
      request<{ ok: true }>('DELETE', `/v1/orgs/${orgId}/groups/${groupId}/members/${userId}`),
    /** Admins only: never owner, never yourself; only owners change owners. */
    changeMemberRole: (orgId: string, userId: string, role: MemberRoleInput['role']) =>
      request<{ role: Role }>('PUT', `/v1/orgs/${orgId}/members/${userId}/role`, { role }),
    removeMember: (orgId: string, userId: string) =>
      request<{ ok: true }>('DELETE', `/v1/orgs/${orgId}/members/${userId}`),
    joinRequests: (orgId: string) =>
      request<{ requests: JoinRequest[] }>('GET', `/v1/orgs/${orgId}/join-requests`),
    approveJoinRequest: (orgId: string, id: string) =>
      request<{ ok: true }>('POST', `/v1/orgs/${orgId}/join-requests/${id}/approve`),
    declineJoinRequest: (orgId: string, id: string) =>
      request<{ ok: true }>('POST', `/v1/orgs/${orgId}/join-requests/${id}/decline`),
    invitations: (orgId: string) =>
      request<{ invitations: Invitation[] }>('GET', `/v1/orgs/${orgId}/invitations`),
    invite: (orgId: string, input: InvitationInput) =>
      request<{ invited: string[]; alreadyMembers: string[] }>(
        'POST',
        `/v1/orgs/${orgId}/invitations`,
        input,
      ),
    resendInvitation: (orgId: string, id: string) =>
      request<{ ok: true }>('POST', `/v1/orgs/${orgId}/invitations/${id}/resend`),
    revokeInvitation: (orgId: string, id: string) =>
      request<{ ok: true }>('DELETE', `/v1/orgs/${orgId}/invitations/${id}`),
    invitation: (token: string) =>
      request<InvitationDetails>('GET', `/v1/public/invitations/${encodeURIComponent(token)}`),
    acceptInvitation: (token: string) =>
      request<{ orgId: string }>('POST', `/v1/invitations/${encodeURIComponent(token)}/accept`),
    admin: {
      overview: () =>
        request<{ pendingRequests: number; users: number; topLevelOrgs: number }>(
          'GET',
          '/v1/admin/overview',
        ),
      plans: () => request<{ plans: Plan[] }>('GET', '/v1/admin/plans'),
      reports: () => request<PlatformReport>('GET', '/v1/admin/reports'),
      userProfile: (userId: string) =>
        request<UserProfile>('GET', `/v1/admin/users/${userId}/profile`),
      orgRequests: (status: OrgRequestStatus | 'all') =>
        request<{ requests: AdminOrgRequest[] }>('GET', `/v1/admin/org-requests?status=${status}`),
      approve: (id: string, input: Partial<ApproveOrgRequestInput>) =>
        request<{ org: Org }>('POST', `/v1/admin/org-requests/${id}/approve`, input),
      reject: (id: string, note: string) =>
        request<{ ok: true }>('POST', `/v1/admin/org-requests/${id}/reject`, { note }),
      users: (query: Partial<AdminUserQuery>) =>
        request<{ users: AdminUser[]; total: number }>(
          'GET',
          `/v1/admin/users${toQueryString(query)}`,
        ),
      orgOptions: () => request<{ orgs: OrgOption[] }>('GET', '/v1/admin/org-options'),
      setCreatorGrant: (userId: string, input: Partial<CreatorGrantInput> & { maxOrgs: number }) =>
        request('PUT', `/v1/admin/users/${userId}/creator-grant`, input),
      revokeCreatorGrant: (userId: string) =>
        request('DELETE', `/v1/admin/users/${userId}/creator-grant`),
      setPlatformAdmin: (userId: string, isAdmin: boolean) =>
        request('PUT', `/v1/admin/users/${userId}/platform-admin`, { isAdmin }),
      setDisplayName: (userId: string, displayName: string) =>
        request('PUT', `/v1/admin/users/${userId}/display-name`, { displayName }),
      setSuspended: (userId: string, suspended: boolean) =>
        request('PUT', `/v1/admin/users/${userId}/suspended`, { suspended }),
      orgs: (q = '') =>
        request<{ orgs: AdminOrg[] }>(
          'GET',
          `/v1/admin/orgs${q ? `?q=${encodeURIComponent(q)}` : ''}`,
        ),
      createOrg: (
        input: Partial<AdminCreateOrgInput> &
          Pick<AdminCreateOrgInput, 'name' | 'type' | 'country' | 'ownerEmail'>,
      ) => request<{ org: Org; ownerInvited: boolean }>('POST', '/v1/admin/orgs', input),
      updateOrg: (orgId: string, input: AdminUpdateOrgInput) =>
        request<Org>('PATCH', `/v1/admin/orgs/${orgId}`, input),
      setOrgVerified: (orgId: string, verified: boolean) =>
        request<void>('PUT', `/v1/admin/orgs/${orgId}/verified`, { verified }),
      setOrgProfileHidden: (orgId: string, hidden: boolean) =>
        request<void>('PUT', `/v1/admin/orgs/${orgId}/profile-hidden`, { hidden }),
      auditLogs: (query: Partial<AuditQuery>) =>
        request<{ logs: AuditEntry[]; total: number; areas: string[] | null }>(
          'GET',
          `/v1/admin/audit-logs${toQueryString(query)}`,
        ),
    },
    join: (code: string) =>
      request<{ orgId: string; role: Role }>('POST', `/v1/join/${encodeURIComponent(code)}`),
  };
}
